/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import { round } from 'lodash'

import type { AccessorFn } from '@elastic/charts'
import { Axis, BarSeries, Chart, LineSeries, Position, ScaleType, Settings } from '@elastic/charts'
import { EuiLoadingChart } from '@elastic/eui'

import { withErrorBoundary } from '@modules/cui/Boundary'
import { colorForInstances } from '@modules/billing-lib/colorGenerator'
import { useElasticChartsTheme } from '@modules/cui/charts/hooks'

import { DeploymentDimensions } from '@/components/User/BillingUsage/constants'

import { ChartType, ProductType, ViewBy } from '../../types'
import EmptyDataPrompt from '../../BillingUsageOverviewV2/EmptyDataPrompt'
import { IconType } from '../../BillingUsageOverviewV2/types'
import ChartTooltip from '../ChartTooltip'

import type { Props } from './types'
import type { WrappedComponentProps } from 'react-intl'
import type { FunctionComponent } from 'react'

const getDataAccessors = (dimensions: Set<string>) => {
  const accessors: AccessorFn[] = []

  dimensions.forEach((dimension) => {
    const accessor: AccessorFn = ({ values }) => {
      const dimensionId = dimension.split('-')[0]
      const dimensionName = dimension.split('-')[1]
      const item = values.find(({ id }) => id === dimensionId)
      accessor.fieldName = item ? item.name : dimensionName
      return item?.value || 0
    }

    accessors.push(accessor)
  })

  return accessors
}

// yAccessor is dimension name in this case
// color keys are lowercase to match the color in table
const colorAccessor = ({ yAccessor }) => colorForInstances.get(yAccessor.toLowerCase())

const chartComponent = {
  [ChartType.BAR]: BarSeries,
  [ChartType.LINE]: LineSeries,
}

const viewByFormat = {
  [ViewBy.DAY]: 'MMM DD',
  [ViewBy.MONTH]: 'MMM YYYY',
}

const stackAccessors = {
  [ChartType.BAR]: ['timestamp'],
  [ChartType.LINE]: [],
}

const emptyChartIcon = {
  [ChartType.BAR]: IconType.BAR_CHART,
  [ChartType.LINE]: IconType.LINE_CHART,
}

const InstancesChart: FunctionComponent<Props & WrappedComponentProps> = ({
  data = [],
  chartType,
  isLoading = false,
  viewBy,
  productType,
}) => {
  const baseTheme = useElasticChartsTheme()

  const dimensions = new Set<string>()

  if (productType === ProductType.Projects) {
    // dynamic generating dimensions for serverless projects
    data.forEach((d) => d.values.forEach((v) => dimensions.add(`${v.id}-${v.name}`)))
  } else {
    DeploymentDimensions.forEach((d) => dimensions.add(`${d.id}-${d.name}`))
  }

  // nosemgrep
  const ChartComponent = chartComponent[chartType]

  if (isLoading) {
    return <EuiLoadingChart size='xl' />
  }

  // Because the x-axis is ordinal, we need to define the domain.
  const xDomain = data.map(({ timestamp }) => timestamp)
  const yAccessors = getDataAccessors(dimensions)

  if (yAccessors.length === 0) {
    return <EmptyDataPrompt iconType={emptyChartIcon[chartType]} />
  }

  return (
    <Chart size={['100%', '100%']}>
      <Settings baseTheme={baseTheme} xDomain={xDomain} showLegend={true} />
      <ChartTooltip />
      <Axis
        id='amount'
        position={Position.Left}
        gridLine={{ visible: true }}
        ticks={6}
        tickFormat={(d) => String(round(Math.abs(d), 4).toFixed(4))} // the same format as in the table below the graph
        title='ECU'
      />
      <Axis
        id='timestamp'
        position={Position.Bottom}
        tickFormat={(d) => moment.unix(d).utc().format(viewByFormat[viewBy])}
        showOverlappingTicks={false}
        showOverlappingLabels={false}
        ticks={4}
      />
      <ChartComponent
        id={dimensions.size === 1 ? Array.from(dimensions)[0]?.split('-')[1] ?? 'usage' : 'usage'}
        xScaleType={ScaleType.Ordinal}
        yScaleType={ScaleType.Linear}
        xAccessor='timestamp'
        yAccessors={yAccessors}
        stackAccessors={stackAccessors[chartType]}
        splitSeriesAccessors={[]}
        data={data}
        color={colorAccessor}
      />
    </Chart>
  )
}

export default injectIntl(withErrorBoundary(InstancesChart))
